import { lazy, Suspense } from 'react';
import LayoutV1, { FullWidthDashboardLayout } from '@/layouts/layout-1';
import LayoutV2 from '@/layouts/layout-2';
import { AuthGuard, ClubAdminGuard } from '@/components/auth';
import { LoadingProgress } from '@/components/loader';
import { useSettings } from '@/hooks/useSettings';

// DASHBOARD PAGES
const Analytics = lazy(() => import('@/pages/dashboard/analytics'));

// TEAMS PAGES
const TeamList = lazy(() => import('@/pages/teams/team-list'));
const TeamInvitations = lazy(() => import('@/pages/teams/team-invitations'));

// PLAYERS PAGES
const PlayerList = lazy(() => import('@/pages/players/player-list'));

// MEDIA PAGES
const MediaLibrary = lazy(() => import('@/pages/media/media-library'));

// SOCIAL CONNECTIONS PAGES
const SocialConnectionsList = lazy(() => import('@/pages/social-connections/social-connections-list'));

// MATCHES PAGES
const MatchList = lazy(() => import('@/pages/matches/match-list'));
const MatchContent = lazy(() => import('@/pages/matches/match-content'));

// CONTENT TYPES PAGES
const ContentTypesList = lazy(() => import('@/pages/content-types/content-types-list'));

// CLUB SETTINGS PAGES
const ClubSettings = lazy(() => import('@/pages/club-settings/club-settings'));

// CONTENT PAGES
const ContentList = lazy(() => import('@/pages/content/content-list'));

// SOCIAL POSTS PAGES
const SocialPostDetail = lazy(() => import('@/pages/social-posts/social-post-detail'));

// GRAPHIC EDITOR PAGES
const GraphicEditor = lazy(() => import('@/pages/graphic-editor/graphic-editor'));

// VIDEO EDITOR PAGES
const VideoEditor = lazy(() => import('@/pages/video-editor/video-editor'));

const ActiveLayout = () => {
  const { settings } = useSettings();
  return (
    <AuthGuard>
      <Suspense fallback={<LoadingProgress />}>
        {settings.activeLayout === 'layout2' ? <LayoutV2 /> : <LayoutV1 />}
      </Suspense>
    </AuthGuard>
  );
};

// Full-width layout for pages like the graphic editor
const FullWidthLayout = () => {
  return (
    <AuthGuard>
      <Suspense fallback={<LoadingProgress />}>
        <FullWidthDashboardLayout />
      </Suspense>
    </AuthGuard>
  );
};

export const DashboardRoutes = [
  {
    path: 'dashboard',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <Analytics />
      }
      // Add more dashboard routes here as needed
      // Copy route patterns from full-version/src/routes/dashboard.jsx
    ]
  },
  {
    path: 'social-connections',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <SocialConnectionsList />
      }
    ]
  },
  {
    path: 'teams',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <ClubAdminGuard><TeamList /></ClubAdminGuard>
      },
      {
        path: ':teamId/invitations',
        element: <ClubAdminGuard><TeamInvitations /></ClubAdminGuard>
      }
    ]
  },
  {
    path: 'players',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <PlayerList />
      }
    ]
  },
  {
    path: 'media',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <MediaLibrary />
      }
    ]
  },
  {
    path: 'matches',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <MatchList />
      },
      {
        path: ':matchId/content',
        element: <MatchContent />
      }
    ]
  },
  {
    path: 'content',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <ContentList />
      }
    ]
  },
  {
    path: 'content-templates',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <ClubAdminGuard><ContentTypesList /></ClubAdminGuard>
      }
    ]
  },
  {
    path: 'club-settings',
    element: <ActiveLayout />,
    children: [
      {
        index: true,
        element: <ClubAdminGuard><ClubSettings /></ClubAdminGuard>
      }
    ]
  },
  {
    path: 'social-posts',
    element: <ActiveLayout />,
    children: [
      {
        path: ':postId',
        element: <SocialPostDetail />
      }
    ]
  },
  {
    path: 'editor',
    element: <FullWidthLayout />,
    children: [
      {
        index: true,
        element: <GraphicEditor />
      },
      {
        path: ':type',
        element: <GraphicEditor />
      },
      {
        path: ':type/:matchId',
        element: <GraphicEditor />
      },
      {
        path: 'template/:templateId',
        element: <GraphicEditor />
      },
      {
        path: 'post/:postId',
        element: <GraphicEditor />
      }
    ]
  },
  {
    path: 'video-editor',
    element: <FullWidthLayout />,
    children: [
      {
        index: true,
        element: <VideoEditor />
      },
      {
        path: 'template/:templateId',
        element: <VideoEditor />
      },
      {
        path: 'post/:postId',
        element: <VideoEditor />
      }
    ]
  }
];
